import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import { useDarkMode } from '../../hooks/useDarkMode';
import Avatar from '../shared/Avatar';

// ─── Step definitions ────────────────────────────────────────────────────────
// Each step maps to one or more DB fields.
// `key` = primary timestamp field; `fields` = additional inputs shown in the complete modal.

const ONBOARDING_STEPS = [
  {
    key: 'step_offer_signed_at',
    label: 'Offer & Contract',
    desc: 'Offer accepted, contract e-signed and filed',
    icon: '📄',
    owner: 'HR',
  },
  {
    key: 'step_new_starter_form_at',
    label: 'New Starter Form',
    desc: 'Personal details, bank details, HMRC tax statement, health declaration',
    icon: '📝',
    owner: 'HR + New Hire',
  },
  {
    key: 'step_bpss_initiated_at',
    label: 'BPSS Initiated',
    desc: 'Checkback International briefed — DBS, right to work & references',
    icon: '🔍',
    owner: 'HR',
    subOf: 'clearance',
  },
  {
    key: 'step_bpss_cleared_at',
    label: 'BPSS Cleared',
    desc: 'Background checks passed',
    icon: '✅',
    owner: 'HR',
    subOf: 'clearance',
  },
  {
    key: 'step_sc_submitted_at',
    label: 'SC Submitted to RR',
    desc: 'BPSS + clearance form + HR justification sent to Rolls-Royce',
    icon: '📨',
    owner: 'HR',
    subOf: 'clearance',
    scOnly: true,
  },
  {
    key: 'step_nsvs_portal_at',
    label: 'NSVS Portal Open',
    desc: 'RR set up portal; candidate notified to complete all sections',
    icon: '🌐',
    owner: 'HR + Candidate',
    subOf: 'clearance',
    scOnly: true,
  },
  {
    key: 'step_uksv_submitted_at',
    label: 'UKSV Portal Submitted',
    desc: 'Candidate completes UKSV portal and submits application for review',
    icon: '📤',
    owner: 'Candidate',
    subOf: 'clearance',
    scOnly: true,
  },
  {
    key: 'step_sc_cleared_at',
    label: 'SC Clearance Granted',
    desc: 'Security clearance granted by NSVS',
    icon: '🔐',
    owner: 'NSVS / RR',
    subOf: 'clearance',
    scOnly: true,
    extraFields: [
      { key: 'clearance_date', label: 'Official Clearance Date', type: 'date' },
    ],
  },
  {
    key: 'step_rr_induction_at',
    label: 'RR Induction / Read-On',
    desc: 'Candidate attends RR Raynesway, collects site pass — officially cleared',
    icon: '🏭',
    owner: 'HR + Candidate',
    subOf: 'clearance',
    scOnly: true,
  },
  {
    key: 'step_laptop_ordered_at',
    label: 'Laptop Ordered',
    desc: 'IT request raised — laptop, peripherals, software spec confirmed',
    icon: '💻',
    owner: 'IT',
    warning: '⚠ Chase IT — laptops frequently delayed',
    extraFields: [
      { key: 'laptop_type', label: 'Laptop Type', type: 'select', options: ['Mac', 'Windows', 'Linux', 'Other'] },
    ],
  },
  {
    key: 'step_laptop_setup_at',
    label: 'Laptop Set Up',
    desc: 'Device imaged, tested and ready. The asset tag becomes their laptop on Hardware → Laptops.',
    icon: '⚙️',
    owner: 'IT',
    extraFields: [
      { key: 'laptop_asset_tag', label: 'Asset Tag', type: 'text', placeholder: 'e.g. AV-10066' },
    ],
  },
  {
    key: 'step_m365_setup_at',
    label: 'M365 Provisioning',
    desc: 'Automatically triggered when Laptop Set Up is completed — creates the M365 account, assigns the licence and sets the manager',
    icon: '⚡',
    owner: 'IT (Auto)',
    autoTrigger: true,
    subOf: 'laptop',
  },
  {
    key: 'step_ops_setup_at',
    label: 'OPS Account Set Up',
    desc: 'Person added to OPS with correct role, team, manager & day rate',
    icon: '🖥',
    owner: 'Admin',
  },
  {
    key: 'probation_3month_date',
    label: 'Probation Dates Set',
    desc: '3-month mid-point and 6-month formal review dates confirmed',
    icon: '📅',
    owner: 'HR',
    dateOnly: true,   // no "mark done" — just set dates
    extraFields: [
      { key: 'probation_3month_date', label: '3-Month Review Date', type: 'date' },
      { key: 'probation_6month_date', label: '6-Month Review (Probation End) Date', type: 'date' },
    ],
  },
  {
    key: 'step_probation_3month_at',
    label: '3-Month Review',
    desc: 'Mid-point probation review completed with manager',
    icon: '📋',
    owner: 'Manager + HR',
    subOf: 'probation',
  },
  {
    key: 'step_probation_6month_at',
    label: 'Probation Sign-Off',
    desc: 'Formal probation outcome recorded — enhanced benefits activated',
    icon: '🎓',
    owner: 'HR',
    subOf: 'probation',
  },
  // ── Candidate Tasks ──────────────────────────────────────────────────────────
  {
    key: 'step_benefits_choices_at',
    label: 'Benefits Choices',
    desc: 'Candidate reviews and selects benefits package — pension contributions, health, cycle to work, etc.',
    icon: '🎁',
    owner: 'HR + Candidate',
    subOf: 'candidate_tasks',
  },
  {
    key: 'step_handbook_confirmed_at',
    label: 'Handbook & Policies Confirmed',
    desc: 'Candidate reads employee handbook and all company policies, confirms understanding in writing',
    icon: '📖',
    owner: 'HR + Candidate',
    subOf: 'candidate_tasks',
  },
  // ── Candidate Day 1 Preparation ──────────────────────────────────────────────
  {
    key: 'step_starter_box_at',
    label: 'New Starter Box',
    desc: 'Branded welcome box prepared and ready for day 1',
    icon: '📦',
    owner: 'HR',
    subOf: 'day1prep',
  },
  {
    key: 'step_first_day_email_at',
    label: 'First Day Email',
    desc: 'Logistics email sent to new starter — location, time, parking, what to bring',
    icon: '📧',
    owner: 'HR',
    subOf: 'day1prep',
  },
  {
    key: 'step_welcome_email_at',
    label: 'Welcome Email',
    desc: 'Company-wide welcome email sent announcing the new starter',
    icon: '💌',
    owner: 'HR / Manager',
    subOf: 'day1prep',
  },
  {
    key: 'step_whatsapp_at',
    label: 'Added to WhatsApp',
    desc: 'New starter added to company and team WhatsApp group(s)',
    icon: '💬',
    owner: 'HR / Manager',
    subOf: 'day1prep',
  },
  {
    key: 'step_team_today_at',
    label: 'Added to Team Today',
    desc: 'New starter added to Team Today platform',
    icon: '📱',
    owner: 'HR',
    subOf: 'day1prep',
  },
  {
    key: 'step_company_meetings_at',
    label: 'Added to Company Meetings',
    desc: 'Added to all recurring company-wide and team meetings',
    icon: '🗓',
    owner: 'HR / Manager',
    subOf: 'day1prep',
  },
  {
    key: 'step_salary_extras_at',
    label: 'Added to Salary Extras',
    desc: 'Cycle to work, parking, childcare vouchers and other salary sacrifice schemes configured',
    icon: '💰',
    owner: 'HR / Finance',
    subOf: 'day1prep',
  },
  {
    key: 'step_payroll_added_at',
    label: 'Added to Payroll',
    desc: 'New starter added to payroll with correct pay details and start date',
    icon: '💷',
    owner: 'Finance',
    subOf: 'day1prep',
  },
  {
    key: 'step_pension_added_at',
    label: 'Added to Pension Scheme',
    desc: 'Auto-enrolled in company pension — contributions and opt-in/out confirmed',
    icon: '🏦',
    owner: 'HR / Finance',
    subOf: 'day1prep',
  },
  {
    key: 'step_documents_folder_at',
    label: 'Employee Documents Folder',
    desc: 'Shareable folder created in SharePoint / OneDrive for employee documents',
    icon: '📁',
    owner: 'Admin / IT',
    subOf: 'day1prep',
  },
  {
    key: 'step_day1_complete_at',
    label: 'Day 1 Sign-Off',
    desc: 'All day-1 checks done, employee settled in',
    icon: '🎉',
    owner: 'HR + Manager',
  },
];

const OFFBOARDING_STEPS = [
  {
    key: 'step_notice_confirmed_at',
    label: 'Notice Confirmed',
    desc: 'Resignation / end of contract confirmed in writing; leave date set',
    icon: '📄',
    owner: 'HR',
    extraFields: [
      { key: 'leave_date', label: 'Last Working Day', type: 'date' },
    ],
  },
  {
    key: 'step_exit_interview_at',
    label: 'Exit Interview',
    desc: 'Exit conversation held and notes filed',
    icon: '💬',
    owner: 'HR / Manager',
  },
  {
    key: 'step_clearance_offboarded_at',
    label: 'Clearance Cancelled / Read-Off',
    desc: 'If uncleared: RR notified to cancel request. If cleared: employee attended RR to sign off and return passes.',
    icon: '🔐',
    owner: 'HR',
  },
  {
    key: 'step_it_access_revoked_at',
    label: 'IT Access Revoked',
    desc: 'Ticked automatically at 18:00 UTC on the last working day once Ops has disabled M365, revoked sign-ins and removed groups. Licences and other SaaS & supplier portals are still removed by hand.',
    icon: '🔒',
    owner: 'IT',
    warning: '⚠ Do not revoke until close of business on final day',
  },
  {
    key: 'step_assets_returned_at',
    label: 'Assets Returned',
    desc: 'Laptop wiped & returned (recorded on Hardware → Laptops), headset, office keys, credit card, RR pass',
    icon: '📦',
    owner: 'IT + Manager',
  },
  {
    key: 'step_final_payroll_at',
    label: 'Final Payroll & P45',
    desc: 'Outstanding holiday paid, deductions processed, P45 issued',
    icon: '💷',
    owner: 'HR / Finance',
  },
  {
    key: 'step_ops_archived_at',
    label: 'OPS Account Archived',
    desc: 'Person archived in OPS — hidden from active lists. Done automatically with IT Access Revoked.',
    icon: '🗃',
    owner: 'Admin',
  },
  {
    key: 'step_offboarding_complete_at',
    label: 'Offboarding Complete',
    desc: 'All checks done, record closed',
    icon: '✅',
    owner: 'HR',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtShort(dt) {
  if (!dt) return null;
  return new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

function completedCount(rec, steps) {
  return steps.filter(s => !s.dateOnly && rec[s.key]).length;
}

function currentStepLabel(rec, steps) {
  const next = steps.find(s => !s.dateOnly && !rec[s.key]);
  return next?.label || 'Complete';
}

// ─── Step circle indicator ────────────────────────────────────────────────────
function StepCircle({ done, active, sub }) {
  const isDark = useDarkMode();
  const size = sub ? 18 : 22;
  if (done) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: '#0D1D32', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, zIndex: 1,
      }}>
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    );
  }
  if (active) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: isDark ? 'rgb(30 41 59)' : '#fff', border: '2.5px solid #E5B87A',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, zIndex: 1,
      }}>
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#E5B87A' }} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: isDark ? 'rgb(30 41 59)' : '#fff', border: `${sub ? 1.5 : 2}px solid ${isDark ? 'rgb(71 85 105)' : '#D1D5DB'}`,
      flexShrink: 0, zIndex: 1,
    }} />
  );
}

// ─── Complete step modal ──────────────────────────────────────────────────────
function CompleteStepModal({ step, record, assets, onSave, onClose }) {
  const isDark = useDarkMode();
  const today = new Date().toISOString().substring(0, 10);
  const [date, setDate] = useState(
    (record[step.key] || '').substring(0, 10) || today
  );
  const [extras, setExtras] = useState(() => {
    const init = {};
    (step.extraFields || []).forEach(f => { init[f.key] = record[f.key] || ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const setExtra = (k, v) => setExtras(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const payload = { [step.key]: date + 'T12:00:00', ...extras };
    await onSave(payload);
    setSaving(false);
  };

  const inp = {
    border: `1px solid ${isDark ? 'rgb(71 85 105)' : '#D1D5DB'}`,
    borderRadius: 5, padding: '5px 8px', fontSize: 10, fontFamily: 'Montserrat',
    width: '100%', boxSizing: 'border-box',
    background: isDark ? 'rgb(15 23 42)' : '#fff',
    color: isDark ? 'rgb(241 245 249)' : '#111',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: isDark ? 'rgb(30 41 59)' : '#fff', borderRadius: 12, width: 380, padding: '20px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: isDark ? '1px solid rgb(51 65 85)' : 'none' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#0D1D32', marginBottom: 4 }}>
          {step.icon} {step.label}
        </div>
        <div style={{ fontSize: 9, color: isDark ? 'rgb(100 116 139)' : '#9CA3AF', marginBottom: step.key === 'step_assets_returned_at' ? 4 : 16 }}>{step.desc}</div>
        {step.key === 'step_assets_returned_at' && <div style={{ marginBottom: 16 }}><LaptopToCollect record={record} done={!!record[step.key]} inDialog /></div>}

        {!step.dateOnly && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 8, fontWeight: 700, color: isDark ? 'rgb(148 163 184)' : '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>
              Completion Date
            </label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
          </div>
        )}

        {(step.extraFields || []).map(f => (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 8, fontWeight: 700, color: isDark ? 'rgb(148 163 184)' : '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>
              {f.label}
            </label>
            {f.type === 'select' ? (
              <select value={extras[f.key] || ''} onChange={e => setExtra(f.key, e.target.value)} style={inp}>
                <option value="">— select —</option>
                {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === 'asset' ? (
              <select value={extras[f.key] || ''} onChange={e => setExtra(f.key, e.target.value)} style={inp}>
                <option value="">— select asset —</option>
                {(() => {
                  const all = assets || [];
                  const isLaptop = a => a.category === 'laptop' || a.category === 'computer' || (a.name || '').toLowerCase().includes('laptop');
                  const laptops = all.filter(isLaptop);
                  const others  = all.filter(a => !isLaptop(a));
                  const fmt = a => `${a.asset_tag ? a.asset_tag + ' · ' : ''}${a.name}${a.serial_number ? ' (' + a.serial_number + ')' : ''}`;
                  if (laptops.length > 0) {
                    return [
                      ...laptops.map(a => <option key={a.id} value={a.id}>{fmt(a)}</option>),
                      ...(others.length > 0 ? [<option key="__sep" disabled>──────────</option>, ...others.map(a => <option key={a.id} value={a.id}>{fmt(a)}</option>)] : []),
                    ];
                  }
                  return others.map(a => <option key={a.id} value={a.id}>{fmt(a)}</option>);
                })()}
              </select>
            ) : (
              <input type={f.type === 'date' ? 'date' : 'text'} value={extras[f.key] || ''} onChange={e => setExtra(f.key, e.target.value)}
                placeholder={f.placeholder || ''} style={inp} />
            )}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '6px 0', fontSize: 9, border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, background: isDark ? 'rgb(51 65 85)' : '#fff', color: isDark ? 'rgb(241 245 249)' : '#111', cursor: 'pointer' }}>Cancel</button>
          {step.dateOnly ? (
            <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '6px 0', fontSize: 9, fontWeight: 700, background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save Dates'}
            </button>
          ) : record[step.key] ? (
            <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '6px 0', fontSize: 9, fontWeight: 700, background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Update'}
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '6px 0', fontSize: 9, fontWeight: 700, background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : '✓ Mark Complete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── New record modal ─────────────────────────────────────────────────────────
// ─── New Onboarding/Offboarding modal — creates person then record ────────────
// Step 1: Person details  |  Step 2: Process settings
const AVATAR_COLORS = ['#1B2A4A','#A1723D','#2E7D32','#1565C0','#6A1B9A','#AD1457','#4E342E','#37474F'];

// Helper: add N months to a YYYY-MM-DD string
function addMonths(dateStr, n) {
  if (!dateStr) return '';
  // Parse as local date (split avoids UTC-midnight timezone shift)
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '';
  let year = parseInt(parts[0], 10);
  let month = parseInt(parts[1], 10) - 1; // 0-indexed
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return '';
  if (year < 1000 || year > 2200) return ''; // guard against partial typing (e.g. year=2)
  month += n;
  while (month > 11) { month -= 12; year += 1; }
  while (month < 0)  { month += 12; year -= 1; }
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

// Format YYYY-MM-DD → "1 Apr 2026"
function fmtDMY(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const d = parseInt(parts[2], 10);
  const m = months[parseInt(parts[1], 10) - 1];
  const y = parts[0];
  if (!m || isNaN(d)) return dateStr;
  return `${d} ${m} ${y}`;
}

function NewRecordModal({ teams, onSave, onClose }) {
  const isDark = useDarkMode();
  const [step, setStep] = useState(1); // 1 = person details, 2 = process settings
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Person fields (Step 1)
  const [person, setPerson] = useState({
    first_name: '', last_name: '', preferred_name: '',
    email_work: '', personal_mobile: '',
    designation: '', team_id: '',
    employment_type: 'permanent', grade: '',
    date_of_joining: '', end_of_employment: '', user_role: 'engineer',
    avatar_color: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)],
  });

  // Process fields (Step 2) — type is always 'onboarding' for this modal
  const [process, setProcess] = useState({
    clearance_target: 'sc',
    m365_license: 'standard',
    probation_3month_date: '', probation_6month_date: '',
    notes: '',
  });

  const autoEmail = (fn, ln) => {
    const f = fn.trim().toLowerCase().replace(/\s+/g, '');
    const l = ln.trim().toLowerCase().replace(/\s+/g, '');
    return f && l ? `${f}.${l}@altonvalley.com` : '';
  };

  const handleLastNameBlur = () => {
    setPerson(p => {
      // Only fill if email is empty or still matches a previously auto-generated value
      const prev = autoEmail(p.first_name, p.last_name);
      if (!p.email_work || p.email_work === prev || p.email_work.endsWith('@altonvalley.com') && p.email_work.startsWith(p.first_name.trim().toLowerCase())) {
        return { ...p, email_work: autoEmail(p.first_name, p.last_name) };
      }
      return p;
    });
  };

  const sp = (k, v) => {
    setPerson(p => {
      const next = { ...p, [k]: v };
      // Auto-populate probation dates when start date changes (only once year is fully typed)
      if (k === 'date_of_joining' && v && v.split('-')[0]?.length === 4) {
        const three = addMonths(v, 3);
        const six   = addMonths(v, 6);
        if (three && six) {
          setProcess(pr => ({
            ...pr,
            probation_3month_date: pr.probation_3month_date || three,
            probation_6month_date: pr.probation_6month_date || six,
          }));
        }
      }
      return next;
    });
  };
  const ss = (k, v) => setProcess(p => ({ ...p, [k]: v }));

  const inp = {
    border: `1px solid ${isDark ? 'rgb(71 85 105)' : '#D1D5DB'}`,
    borderRadius: 5, padding: '5px 8px', fontSize: 10, fontFamily: 'Montserrat',
    width: '100%', boxSizing: 'border-box',
    background: isDark ? 'rgb(15 23 42)' : '#fff',
    color: isDark ? 'rgb(241 245 249)' : '#111',
  };
  const lbl = { display: 'block', fontSize: 8, fontWeight: 700, color: isDark ? 'rgb(148 163 184)' : '#6B7280', textTransform: 'uppercase', marginBottom: 3 };
  const field = { marginBottom: 10 };

  const canProceed = person.first_name.trim() && person.last_name.trim() && person.email_work.trim();

  const handleNext = () => { setErr(''); setStep(2); };

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      // An end date only means something on a fixed-term contract; one typed
      // before switching the type back is not kept.
      await onSave({ person: person.employment_type === 'fixed_term' ? person : { ...person, end_of_employment: '' }, process });
    } catch (e) {
      setErr(e.message || 'Failed to create record');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: isDark ? 'rgb(30 41 59)' : '#fff', borderRadius: 12, width: 520, maxHeight: '90vh', overflowY: 'auto', padding: '20px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: isDark ? '1px solid rgb(51 65 85)' : 'none' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#0D1D32' }}>
              {step === 1 ? 'New Starter — Person Details' : 'New Starter — Onboarding Settings'}
            </div>
            <div style={{ fontSize: 9, color: isDark ? 'rgb(100 116 139)' : '#9CA3AF', marginTop: 2 }}>Step {step} of 2</div>
          </div>
          {/* Step indicators */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2].map(n => (
              <div key={n} style={{
                width: 24, height: 24, borderRadius: '50%', fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step === n ? '#0D1D32' : step > n ? '#B8966E' : isDark ? 'rgb(51 65 85)' : '#E5E7EB',
                color: step >= n ? '#fff' : isDark ? 'rgb(148 163 184)' : '#6B7280',
              }}>{n}</div>
            ))}
          </div>
        </div>

        {/* ── STEP 1: Person Details ── */}
        {step === 1 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 2 }}>
              <div style={field}>
                <label style={lbl}>First Name *</label>
                <input value={person.first_name} onChange={e => sp('first_name', e.target.value)} style={inp} placeholder="Jane" />
              </div>
              <div style={field}>
                <label style={lbl}>Last Name *</label>
                <input value={person.last_name} onChange={e => sp('last_name', e.target.value)} onBlur={handleLastNameBlur} style={inp} placeholder="Smith" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={field}>
                <label style={lbl}>Preferred Name</label>
                <input value={person.preferred_name} onChange={e => sp('preferred_name', e.target.value)} style={inp} placeholder="Optional" />
              </div>
              <div style={field}>
                <label style={lbl}>Work Email *</label>
                <input type="email" value={person.email_work} onChange={e => sp('email_work', e.target.value)} style={inp} placeholder="jane.smith@altonvalley.com" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={field}>
                <label style={lbl}>Mobile</label>
                <input value={person.personal_mobile} onChange={e => sp('personal_mobile', e.target.value)} style={inp} placeholder="+44 7..." />
              </div>
              <div style={field}>
                <label style={lbl}>Job Title / Designation</label>
                <input value={person.designation} onChange={e => sp('designation', e.target.value)} style={inp} placeholder="e.g. Security Analyst" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={field}>
                <label style={lbl}>Team</label>
                <select value={person.team_id} onChange={e => sp('team_id', e.target.value)} style={inp}>
                  <option value="">— none —</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={field}>
                <label style={lbl}>Grade / Band</label>
                <input value={person.grade} onChange={e => sp('grade', e.target.value)} style={inp} placeholder="e.g. SC3" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div style={field}>
                <label style={lbl}>Employment Type</label>
                <select value={person.employment_type} onChange={e => sp('employment_type', e.target.value)} style={inp}>
                  <option value="permanent">Permanent</option>
                  <option value="contractor">Contractor</option>
                  <option value="fixed_term">Fixed Term</option>
                </select>
              </div>
              <div style={field}>
                <label style={lbl}>Start Date</label>
                <input type="date" value={person.date_of_joining} onChange={e => sp('date_of_joining', e.target.value)} style={inp} />
              </div>
              {person.employment_type === 'fixed_term' && (
                <div style={field}>
                  <label style={lbl}>Contract Ends</label>
                  <input type="date" value={person.end_of_employment} min={person.date_of_joining || undefined} onChange={e => sp('end_of_employment', e.target.value)} style={inp} />
                </div>
              )}
              <div style={field}>
                <label style={lbl}>System Role</label>
                <select value={person.user_role} onChange={e => sp('user_role', e.target.value)} style={inp}>
                  <option value="engineer">Engineer</option>
                  <option value="pm">PM</option>
                  <option value="hr">HR</option>
                  <option value="finance">Finance</option>
                  <option value="billing">Billing</option>
                  <option value="admin">Admin</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>
            </div>
            {/* Avatar colour picker */}
            <div style={{ ...field, marginBottom: 16 }}>
              <label style={lbl}>Avatar Colour</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {AVATAR_COLORS.map(c => (
                  <div key={c} onClick={() => sp('avatar_color', c)} style={{
                    width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
                    boxShadow: person.avatar_color === c ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : 'none',
                  }} />
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── STEP 2: Onboarding Settings ── */}
        {step === 2 && (
          <>
            {/* Summary card */}
            <div style={{ background: isDark ? 'rgb(15 23 42)' : '#F0F4F8', borderRadius: 8, padding: '8px 12px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: person.avatar_color, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>
                {person.first_name[0]}{person.last_name[0]}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#0D1D32' }}>{person.first_name} {person.last_name}</div>
                <div style={{ fontSize: 9, color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>
                  {[person.designation, person.email_work].filter(Boolean).join(' · ')}
                </div>
                {person.date_of_joining && (
                  <div style={{ fontSize: 9, color: '#6B7280' }}>Starts {fmtDMY(person.date_of_joining)}</div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 2 }}>
              <div style={{ ...field }}>
                <label style={lbl}>Clearance Required</label>
                <select value={process.clearance_target} onChange={e => ss('clearance_target', e.target.value)} style={inp}>
                  <option value="sc">BPSS + SC (most roles)</option>
                  <option value="bpss">BPSS Only (e.g. Finance / non-ops)</option>
                </select>
              </div>
              <div style={{ ...field }}>
                <label style={lbl}>M365 License</label>
                <select value={process.m365_license} onChange={e => ss('m365_license', e.target.value)} style={inp}>
                  <option value="standard">Business Standard</option>
                  <option value="premium">Business Premium</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 2 }}>
              <div style={field}>
                <label style={lbl}>3-Month Probation Review</label>
                <input type="date" value={process.probation_3month_date} onChange={e => ss('probation_3month_date', e.target.value)} style={inp} />
                {person.date_of_joining && !process.probation_3month_date && (
                  <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 2 }}>Auto: {addMonths(person.date_of_joining, 3)}</div>
                )}
              </div>
              <div style={field}>
                <label style={lbl}>6-Month Probation Review</label>
                <input type="date" value={process.probation_6month_date} onChange={e => ss('probation_6month_date', e.target.value)} style={inp} />
                {person.date_of_joining && !process.probation_6month_date && (
                  <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 2 }}>Auto: {addMonths(person.date_of_joining, 6)}</div>
                )}
              </div>
            </div>

            <div style={{ ...field, marginBottom: 16 }}>
              <label style={lbl}>Notes (optional)</label>
              <textarea value={process.notes} onChange={e => ss('notes', e.target.value)} rows={3} style={inp} placeholder="Any context, instructions or reminders for the HR team…" />
            </div>
          </>
        )}

        {err && <div style={{ color: '#DC2626', fontSize: 9, marginBottom: 10 }}>{err}</div>}

        {/* Footer buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          {step === 1 ? (
            <>
              <button onClick={onClose} style={{ flex: 1, padding: '7px 0', fontSize: 9, border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, background: isDark ? 'rgb(51 65 85)' : '#fff', color: isDark ? 'rgb(241 245 249)' : '#111', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleNext} disabled={!canProceed} style={{ flex: 2, padding: '7px 0', fontSize: 9, fontWeight: 700, background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: canProceed ? 1 : 0.4 }}>
                Next → Process Settings
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: '7px 0', fontSize: 9, border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, background: isDark ? 'rgb(51 65 85)' : '#fff', color: isDark ? 'rgb(241 245 249)' : '#111', cursor: 'pointer' }}>← Back</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '7px 0', fontSize: 9, fontWeight: 700, background: '#B8966E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Creating…' : '✓ Create New Starter'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Leaver actions: what the 18:00 job will do, or did ───────────────────────
// Shown on the IT Access Revoked step, the one the job ticks. Before the day it
// says what will happen and when; afterwards, what actually did, so a failure is
// on the record and not only in the email.
function LeaverActionsNote({ record }) {
  if (record.type !== 'offboarding') return null;
  let summary = null;
  try { summary = record.leaver_actions_summary ? JSON.parse(record.leaver_actions_summary) : null; } catch { /* unreadable: show the plan */ }
  const box = 'mt-1 rounded border px-2 py-1 text-[8px] leading-snug';

  if (!summary) {
    if (record.leaver_actions_at) return null; // handled before the job existed
    if (!record.leave_date) {
      return <div className={`${box} bg-gray-50 border-gray-200 text-gray-600`}>Set the Last Working Day on Notice Confirmed and Ops removes access automatically at the end of it.</div>;
    }
    if (record.status === 'on_hold' || record.status === 'cancelled') {
      return <div className={`${box} bg-amber-50 border-amber-200 text-amber-800`}>Automatic leaver actions will not run while this record is {record.status === 'on_hold' ? 'On Hold' : 'Cancelled'}.</div>;
    }
    return (
      <div className={`${box} bg-blue-50 border-blue-200 text-blue-800`}>
        <strong>Automatic on {fmtShort(record.leave_date)} at 18:00 UTC</strong> (19:00 in summer): M365 account disabled, sign-ins revoked, groups removed; archived in OPS; this step and OPS Account Archived ticked; a summary emailed to HR.
      </div>
    );
  }

  const m = summary.m365 || {};
  const done = !!record.leaver_actions_at;
  const tone = m.error ? 'bg-red-50 border-red-200 text-red-800' : done ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800';
  const list = (xs) => (xs && xs.length ? xs.join(', ') : 'none');
  return (
    <div className={`${box} ${tone}`}>
      <div className="font-semibold">Leaver actions {fmtShort(summary.attempted_at)}{!done && ' (to be retried at 18:00 UTC)'}</div>
      {m.disabled && <div>M365 disabled; sign-ins {m.sessions_revoked ? 'revoked' : 'NOT revoked'}; groups left: {list(m.groups_left)}</div>}
      {m.no_account && <div>No M365 account found, so nothing to disable</div>}
      {m.error && <div>M365 not disabled: {m.error}</div>}
      <div>Archived in OPS{summary.ops?.end_of_employment_set ? `; End of Employment set to ${fmtShort(summary.ops.end_of_employment_set)}` : ''}</div>
    </div>
  );
}

// The laptop on an offboarding record (Hardware → Laptops): what to collect, or
// what Assets Returned recorded. `inDialog` words it for the Mark Done dialog,
// where ticking the step is what records the return.
function LaptopToCollect({ record, done, inDialog = false }) {
  if (record.type !== 'offboarding' || !record.laptops) return null;
  const { to_collect: items = [], returned } = record.laptops;
  const box = 'mt-1 rounded border px-2 py-1 text-[8px] leading-snug';
  const name = (l) => [l.asset_tag, l.model && `(${l.model})`].filter(Boolean).join(' ');
  const assigned = items.filter(l => l.source === 'assigned');
  const fromPdq = items.filter(l => l.source === 'pdq');

  if (!items.length) {
    if (done && returned) return <div className={`${box} bg-emerald-50 border-emerald-200 text-emerald-800`}>Laptop {returned.asset_tag} recorded as returned on Hardware → Laptops.</div>;
    return <div className={`${box} bg-gray-50 border-gray-200 text-gray-600`}>No laptop assigned on Ops. Check Hardware → Laptops before marking this done.</div>;
  }
  return (
    <div className={`${box} bg-amber-50 border-amber-200 text-amber-800`}>
      {assigned.map(l => (
        <div key={l.asset_tag}><strong>Laptop to collect: {name(l)}</strong>{l.serial_number ? `, S/N ${l.serial_number}` : ''}.{inDialog ? ' Marking this done records it as returned.' : ''}</div>
      ))}
      {fromPdq.map(l => (
        <div key={l.asset_tag}>{assigned.length ? 'Also signed in to' : 'Not assigned on Ops, but signed in to'} {name(l)}, per PDQ. Collect it too; record it on Hardware → Laptops.</div>
      ))}
    </div>
  );
}

// ─── Timeline step row ────────────────────────────────────────────────────────
function StepRow({ step, record, isNext, assets, onComplete, onUndo, onRetryM365, retrying, isLast, depth = 0 }) {
  const isDark = useDarkMode();
  const done = !!record[step.key];
  const active = isNext && !done;
  const isSub = depth > 0;

  return (
    <div style={{ display: 'flex', gap: 0, position: 'relative' }}>
      {/* Vertical connector line */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: isSub ? 40 : 32, flexShrink: 0 }}>
        {!isLast && (
          <div style={{
            position: 'absolute', top: isSub ? 18 : 22, bottom: 0,
            left: isSub ? 19 : 15,
            width: 2,
            background: done ? '#0D1D32' : isDark ? 'rgb(51 65 85)' : '#E5E7EB',
            zIndex: 0,
          }} />
        )}
        <div style={{ marginTop: isSub ? 0 : 0 }}>
          <StepCircle done={done} active={active} sub={isSub} />
        </div>
      </div>

      {/* Step content */}
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 16, paddingLeft: 10, paddingTop: isSub ? 0 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: isSub ? 9 : 10, fontWeight: done ? 600 : 500, color: done ? (isDark ? 'rgb(241 245 249)' : '#0D1D32') : active ? (isDark ? 'rgb(203 213 225)' : '#374151') : '#9CA3AF' }}>
                {step.icon} {step.label}
              </span>
              <span style={{ fontSize: 7, color: '#D1D5DB', fontWeight: 500 }}>{step.owner}</span>
              {step.warning && !done && (
                <span style={{ fontSize: 7, color: '#B45309', background: '#FEF3C7', borderRadius: 4, padding: '1px 5px' }}>{step.warning}</span>
              )}
            </div>
            {(done || active) && (
              <div style={{ fontSize: 8, color: '#9CA3AF', marginTop: 1 }}>{step.desc}</div>
            )}
            {done && (
              <div style={{ fontSize: 8, color: '#059669', fontWeight: 600, marginTop: 2 }}>✓ {fmtShort(record[step.key])}</div>
            )}
            {/* Show extra field values when done */}
            {done && step.extraFields?.map(f => record[f.key] && (
              <div key={f.key} style={{ fontSize: 8, color: '#6B7280', marginTop: 1 }}>
                {f.label}: <span style={{ color: '#374151', fontWeight: 500 }}>{record[f.key]}</span>
              </div>
            ))}
            {/* M365 provisioning details */}
            {step.autoTrigger && done && (!record.m365_provisioning_error || record.m365_user_id) && (
              <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ fontSize: 8, color: '#6B7280' }}>
                  License: <span style={{ fontWeight: 600, color: '#374151' }}>{record.m365_license === 'premium' ? 'Business Premium' : 'Business Standard'}</span>
                </div>
                <div style={{ fontSize: 8 }}>
                  Account: {record.m365_account_enabled
                    ? <span style={{ fontWeight: 600, color: '#059669' }}>✓ Active</span>
                    : <span style={{ fontWeight: 600, color: '#D97706' }}>⏳ Pending — activates on start date</span>
                  }
                </div>
                {record.m365_user_id && (
                  <div style={{ fontSize: 8, color: '#6B7280' }}>
                    Entra ID: <span style={{ fontWeight: 500, color: '#374151', fontFamily: 'monospace' }}>{record.m365_user_id}</span>
                  </div>
                )}
                {record.m365_temp_password && (
                  <div style={{ marginTop: 2, background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 4, padding: '4px 8px' }}>
                    <div style={{ fontSize: 7, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', marginBottom: 1 }}>Temp Password — share with new starter</div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#0D1D32', fontFamily: 'monospace', letterSpacing: 1 }}>{record.m365_temp_password}</div>
                    <div style={{ fontSize: 7, color: '#92400E', marginTop: 1 }}>User will be prompted to change on first sign-in</div>
                  </div>
                )}
              </div>
            )}
            {step.autoTrigger && record.m365_provisioning_error && (
              <div style={{
                marginTop: 4, borderRadius: 4, padding: '4px 8px', fontSize: 8,
                ...(record.m365_user_id
                  ? { background: '#FFFBEB', border: '1px solid #FCD34D', color: '#92400E' }
                  : { background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' })
              }}>
                {record.m365_provisioning_error}
              </div>
            )}
            {/* Leaver cron result: account disabled but something left for IT to clear */}
            {step.key === 'step_it_access_revoked_at' && record.m365_deprovisioning_warning && (
              <div style={{ marginTop: 4, borderRadius: 4, padding: '4px 8px', fontSize: 8, background: '#FFFBEB', border: '1px solid #FCD34D', color: '#92400E' }}>
                {record.m365_deprovisioning_warning}
              </div>
            )}
            {step.key === 'step_it_access_revoked_at' && <LeaverActionsNote record={record} />}
            {step.key === 'step_assets_returned_at' && <LaptopToCollect record={record} done={done} />}
            {/* Probation dates display */}
            {step.key === 'probation_3month_date' && (record.probation_3month_date || record.probation_6month_date) && (
              <div style={{ display: 'flex', gap: 12, marginTop: 3 }}>
                {record.probation_3month_date && <span style={{ fontSize: 8, color: '#6B7280' }}>3-month: <strong>{fmtShort(record.probation_3month_date)}</strong></span>}
                {record.probation_6month_date && <span style={{ fontSize: 8, color: '#6B7280' }}>6-month: <strong>{fmtShort(record.probation_6month_date)}</strong></span>}
              </div>
            )}
          </div>
          {step.autoTrigger ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
              {record.m365_provisioning_error ? (
                <>
                  {record.m365_user_id ? (
                    <span style={{ padding: '3px 10px', fontSize: 8, fontWeight: 600, borderRadius: 5, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}>
                      ⚠ Check
                    </span>
                  ) : (
                    <span style={{ padding: '3px 10px', fontSize: 8, fontWeight: 600, borderRadius: 5, background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA' }}>
                      ✗ Failed
                    </span>
                  )}
                  <button onClick={onRetryM365} disabled={retrying} style={{ padding: '2px 8px', fontSize: 7, fontWeight: 600, borderRadius: 4, cursor: 'pointer', background: '#0D1D32', color: '#fff', border: 'none', opacity: retrying ? 0.5 : 1, fontFamily: 'Montserrat' }}>
                    {retrying ? 'Retrying…' : '↻ Retry'}
                  </button>
                </>
              ) : (
                <span style={{
                  padding: '3px 10px', fontSize: 8, fontWeight: 600, borderRadius: 5,
                  background: done ? '#D1FAE5' : '#F0F4FF',
                  color: done ? '#065F46' : '#6366F1',
                  border: done ? '1px solid #A7F3D0' : '1px solid #C7D2FE',
                }}>
                  {done ? '⚡ Triggered' : '⚡ Auto'}
                </span>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {done && !step.dateOnly && (
                <button
                  onClick={() => onUndo(step)}
                  style={{ padding: '3px 8px', fontSize: 8, fontWeight: 600, borderRadius: 5, cursor: 'pointer', fontFamily: 'Montserrat', background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}
                >
                  ↩
                </button>
              )}
              <button
                onClick={() => onComplete(step)}
                style={{
                  padding: '3px 10px', fontSize: 8, fontWeight: 600, borderRadius: 5, cursor: 'pointer', fontFamily: 'Montserrat',
                  background: done ? '#F3F4F6' : active ? '#0D1D32' : '#F9FAFB',
                  color: done ? '#6B7280' : active ? '#fff' : '#9CA3AF',
                  border: done ? '1px solid #E5E7EB' : active ? 'none' : '1px solid #E5E7EB',
                }}
              >
                {step.dateOnly ? (done ? 'Edit Dates' : 'Set Dates') : done ? 'Edit' : active ? '✓ Mark Done' : 'Mark Done'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────
function RecordDetail({ record: initialRecord, assets, onUpdate, onClose, onDelete }) {
  const isDark = useDarkMode();
  const { toast } = useToast();
  const [record, setRecord]         = useState(initialRecord);
  const [completing, setCompleting] = useState(null);
  const [editNotes, setEditNotes]   = useState(false);
  const [notes, setNotes]           = useState(initialRecord.notes || '');
  const [saving, setSaving]         = useState(false);
  const [retrying, setRetrying]     = useState(false);
  const [editSettings, setEditSettings] = useState(false);
  const [settings, setSettings]     = useState({
    clearance_target: initialRecord.clearance_target || 'sc',
    m365_license:     initialRecord.m365_license     || 'standard',
  });

  useEffect(() => {
    setRecord(initialRecord);
    setNotes(initialRecord.notes || '');
    setSettings({ clearance_target: initialRecord.clearance_target || 'sc', m365_license: initialRecord.m365_license || 'standard' });
  }, [initialRecord]);

  const steps = record.type === 'onboarding' ? ONBOARDING_STEPS : OFFBOARDING_STEPS;
  // For onboarding, filter SC steps if clearance_target is 'bpss'
  const visibleSteps = steps.filter(s => !s.scOnly || record.clearance_target === 'sc');

  const doneCount   = visibleSteps.filter(s => !s.dateOnly && record[s.key]).length;
  const actionCount = visibleSteps.filter(s => !s.dateOnly).length;
  const pct         = actionCount > 0 ? Math.round((doneCount / actionCount) * 100) : 0;
  const nextStepIndex = visibleSteps.findIndex(s => !s.dateOnly && !record[s.key]);

  const handleComplete = async (payload) => {
    setSaving(true);
    try {
      const updated = await api.put(`onboarding/${record.id}`, payload);
      setRecord(updated);
      onUpdate(updated);
      setCompleting(null);
      // If Laptop Set Up was just completed, poll after a short delay so the
      // background M365 provisioning result (step_m365_setup_at, temp password, etc.) shows up
      if ('step_laptop_setup_at' in payload && payload.step_laptop_setup_at && !record.step_laptop_setup_at) {
        setTimeout(async () => {
          try {
            const refreshed = await api.get(`onboarding/${record.id}`);
            setRecord(refreshed);
            onUpdate(refreshed);
          } catch { /* silent */ }
        }, 4000);
      }
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    const updated = await api.put(`onboarding/${record.id}`, { notes });
    setRecord(updated);
    onUpdate(updated);
    setEditNotes(false);
  };

  const handleStatusChange = async (status) => {
    const updated = await api.put(`onboarding/${record.id}`, { status });
    setRecord(updated);
    onUpdate(updated);
  };

  const handleRetryM365 = async () => {
    setRetrying(true);
    try {
      const updated = await api.post(`onboarding/${record.id}/retry-m365`, {});
      setRecord(updated);
      onUpdate(updated);
    } catch (e) {
      toast.error('Retry failed: ' + e.message);
    } finally {
      setRetrying(false);
    }
  };

  const handleSaveSettings = async () => {
    const updated = await api.put(`onboarding/${record.id}`, settings);
    setRecord(updated);
    onUpdate(updated);
    setEditSettings(false);
  };

  const handleUndo = async (step) => {
    if (!confirm(`Undo "${step.label}"? This will clear the completion date.`)) return;
    const updated = await api.put(`onboarding/${record.id}`, { [step.key]: null });
    setRecord(updated);
    onUpdate(updated);
  };

  const isComplete = pct === 100 || record.status === 'complete';
  const typeColor = record.type === 'onboarding' ? { bg: '#EFF6FF', text: '#1D4ED8', dot: '#60A5FA' } : { bg: '#FEF3C7', text: '#92400E', dot: '#FCD34D' };

  // Group steps: separate "clearance", "probation", "candidate_tasks", and "day1prep" sub-steps
  const renderSteps = () => {
    const rendered = [];
    let clearanceGroupShown     = false;
    let probationGroupShown     = false;
    let candidateTasksGroupShown = false;
    let day1prepGroupShown      = false;
    let i = 0;

    for (i = 0; i < visibleSteps.length; i++) {
      const step = visibleSteps[i];
      const isNext = i === nextStepIndex;
      const isLast = i === visibleSteps.length - 1;

      if (step.subOf === 'clearance' && !clearanceGroupShown) {
        clearanceGroupShown = true;
        const clearanceSteps = visibleSteps.filter(s => s.subOf === 'clearance');
        const allClearanceDone = clearanceSteps.every(s => !!record[s.key]);
        rendered.push(
          <div key="clearance-group" style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingLeft: 2 }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                🔐 Clearance {record.clearance_target === 'bpss' ? '(BPSS only)' : '(BPSS + SC)'}
              </span>
              {allClearanceDone && <span style={{ fontSize: 7, background: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>Cleared</span>}
            </div>
            <div style={{ paddingLeft: 16, borderLeft: '2px solid #F3F4F6', marginLeft: 7 }}>
              {clearanceSteps.map((cs, ci) => (
                <StepRow key={cs.key} step={cs} record={record} assets={assets}
                  isNext={visibleSteps.indexOf(cs) === nextStepIndex}
                  isLast={ci === clearanceSteps.length - 1}
                  depth={1} onComplete={setCompleting} onUndo={handleUndo}
                />
              ))}
            </div>
          </div>
        );
        while (i + 1 < visibleSteps.length && visibleSteps[i + 1]?.subOf === 'clearance') i++;
        continue;
      }

      if (step.subOf === 'probation' && !probationGroupShown) {
        probationGroupShown = true;
        const probSteps = visibleSteps.filter(s => s.subOf === 'probation');
        rendered.push(
          <div key="probation-group" style={{ marginBottom: 4 }}>
            <div style={{ paddingLeft: 16, borderLeft: '2px solid #F3F4F6', marginLeft: 7 }}>
              {probSteps.map((ps, pi) => (
                <StepRow key={ps.key} step={ps} record={record} assets={assets}
                  isNext={visibleSteps.indexOf(ps) === nextStepIndex}
                  isLast={pi === probSteps.length - 1}
                  depth={1} onComplete={setCompleting} onUndo={handleUndo}
                />
              ))}
            </div>
          </div>
        );
        while (i + 1 < visibleSteps.length && visibleSteps[i + 1]?.subOf === 'probation') i++;
        continue;
      }

      if (step.subOf === 'candidate_tasks' && !candidateTasksGroupShown) {
        candidateTasksGroupShown = true;
        const ctSteps   = visibleSteps.filter(s => s.subOf === 'candidate_tasks');
        const allCtDone = ctSteps.every(s => !!record[s.key]);
        const ctDone    = ctSteps.filter(s => !!record[s.key]).length;
        rendered.push(
          <div key="candidate-tasks-group" style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingLeft: 2 }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                🎯 Candidate Tasks
              </span>
              {allCtDone
                ? <span style={{ fontSize: 7, background: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>All Done</span>
                : ctDone > 0
                  ? <span style={{ fontSize: 7, background: '#FEF3C7', color: '#92400E', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>{ctDone}/{ctSteps.length}</span>
                  : null
              }
            </div>
            <div style={{ paddingLeft: 16, borderLeft: '2px solid #F3F4F6', marginLeft: 7 }}>
              {ctSteps.map((cs, ci) => (
                <StepRow key={cs.key} step={cs} record={record} assets={assets}
                  isNext={visibleSteps.indexOf(cs) === nextStepIndex}
                  isLast={ci === ctSteps.length - 1}
                  depth={1} onComplete={setCompleting} onUndo={handleUndo}
                />
              ))}
            </div>
          </div>
        );
        while (i + 1 < visibleSteps.length && visibleSteps[i + 1]?.subOf === 'candidate_tasks') i++;
        continue;
      }

      if (step.subOf === 'day1prep' && !day1prepGroupShown) {
        day1prepGroupShown = true;
        const day1Steps   = visibleSteps.filter(s => s.subOf === 'day1prep');
        const allDay1Done = day1Steps.every(s => !!record[s.key]);
        const day1Done    = day1Steps.filter(s => !!record[s.key]).length;
        rendered.push(
          <div key="day1prep-group" style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingLeft: 2 }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                📋 Candidate Day 1 Preparation
              </span>
              {allDay1Done
                ? <span style={{ fontSize: 7, background: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>All Done</span>
                : day1Done > 0
                  ? <span style={{ fontSize: 7, background: '#FEF3C7', color: '#92400E', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>{day1Done}/{day1Steps.length}</span>
                  : null
              }
            </div>
            <div style={{ paddingLeft: 16, borderLeft: '2px solid #F3F4F6', marginLeft: 7 }}>
              {day1Steps.map((ds, di) => (
                <StepRow key={ds.key} step={ds} record={record} assets={assets}
                  isNext={visibleSteps.indexOf(ds) === nextStepIndex}
                  isLast={di === day1Steps.length - 1}
                  depth={1} onComplete={setCompleting} onUndo={handleUndo}
                />
              ))}
            </div>
          </div>
        );
        while (i + 1 < visibleSteps.length && visibleSteps[i + 1]?.subOf === 'day1prep') i++;
        continue;
      }

      if (!step.subOf) {
        const laptopChildren = step.key === 'step_laptop_setup_at'
          ? visibleSteps.filter(s => s.subOf === 'laptop')
          : [];
        rendered.push(
          <StepRow key={step.key} step={step} record={record} assets={assets}
            isNext={isNext} isLast={isLast && laptopChildren.length === 0} depth={0}
            onComplete={setCompleting} onUndo={handleUndo}
            onRetryM365={handleRetryM365} retrying={retrying} />
        );
        if (laptopChildren.length > 0) {
          rendered.push(
            <div key="laptop-children" style={{ paddingLeft: 16, borderLeft: '2px solid #F3F4F6', marginLeft: 7, marginTop: -8, marginBottom: 4 }}>
              {laptopChildren.map((ls, li) => (
                <StepRow key={ls.key} step={ls} record={record} assets={assets}
                  isNext={visibleSteps.indexOf(ls) === nextStepIndex}
                  isLast={li === laptopChildren.length - 1}
                  depth={1} onComplete={setCompleting} onUndo={handleUndo}
                  onRetryM365={handleRetryM365} retrying={retrying}
                />
              ))}
            </div>
          );
        }
      }
    }
    return rendered;
  };

  return (
    <div style={{ fontFamily: 'Montserrat, sans-serif', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${isDark ? 'rgb(51 65 85)' : '#F3F4F6'}`, background: isDark ? 'rgb(15 23 42)' : '#F9FAFB', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 8, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: typeColor.bg, color: typeColor.text }}>
                {record.type === 'onboarding' ? '▲ Onboarding' : '▼ Offboarding'}
              </span>
              <span style={{ fontSize: 8, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: record.status === 'complete' ? '#D1FAE5' : record.status === 'on_hold' ? '#FEF3C7' : record.status === 'cancelled' ? '#F3F4F6' : '#EFF6FF',
                color: record.status === 'complete' ? '#065F46' : record.status === 'on_hold' ? '#92400E' : record.status === 'cancelled' ? '#6B7280' : '#1D4ED8' }}>
                {record.status === 'in_progress' ? 'In Progress' : record.status === 'complete' ? 'Complete' : record.status === 'on_hold' ? 'On Hold' : record.status === 'cancelled' ? 'Cancelled' : record.status}
              </span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: isDark ? 'rgb(241 245 249)' : '#0D1D32' }}>{record.person_name}</div>
            <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 1 }}>{record.person_role}{record.person_team ? ` · ${record.person_team}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ width: 24, height: 24, borderRadius: '50%', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, background: isDark ? 'rgb(30 41 59)' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#9CA3AF', flexShrink: 0 }}>✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: '#6B7280', marginBottom: 4 }}>
            <span>{doneCount} of {actionCount} steps complete</span>
            <span style={{ fontWeight: 700, color: pct === 100 ? '#059669' : '#0D1D32' }}>{pct}%</span>
          </div>
          <div style={{ height: 5, background: isDark ? 'rgb(51 65 85)' : '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#059669' : '#0D1D32', borderRadius: 3, transition: 'width 0.4s ease' }} />
          </div>
        </div>

        {/* Status actions */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {record.status !== 'complete' && record.status !== 'cancelled' && (
            <button
              onClick={() => pct === 100 ? handleStatusChange('complete') : undefined}
              disabled={pct < 100}
              title={pct < 100 ? `${actionCount - doneCount} step${actionCount - doneCount !== 1 ? 's' : ''} still to complete` : 'Mark as complete'}
              style={{ padding: '3px 10px', fontSize: 7, fontWeight: 600, background: pct === 100 ? '#059669' : '#D1D5DB', color: pct === 100 ? '#fff' : '#9CA3AF', border: 'none', borderRadius: 5, cursor: pct === 100 ? 'pointer' : 'not-allowed' }}>
              ✓ Mark Complete{pct < 100 ? ` (${actionCount - doneCount} remaining)` : ''}
            </button>
          )}
          {record.status === 'in_progress' && (
            <button onClick={() => handleStatusChange('on_hold')} style={{ padding: '3px 10px', fontSize: 7, fontWeight: 600, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 5, cursor: 'pointer' }}>
              ⏸ Put On Hold
            </button>
          )}
          {record.status === 'on_hold' && (
            <button onClick={() => handleStatusChange('in_progress')} style={{ padding: '3px 10px', fontSize: 7, fontWeight: 600, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 5, cursor: 'pointer' }}>
              ▶ Resume
            </button>
          )}
          {(record.status === 'in_progress' || record.status === 'on_hold') && (
            <button onClick={() => { if (confirm('Cancel this onboarding? The record will be archived and the person marked inactive.')) handleStatusChange('cancelled'); }}
              style={{ padding: '3px 10px', fontSize: 7, fontWeight: 600, background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 5, cursor: 'pointer' }}>
              ✕ Cancel Onboarding
            </button>
          )}
          {record.status === 'cancelled' && (
            <button onClick={() => handleStatusChange('in_progress')} style={{ padding: '3px 10px', fontSize: 7, fontWeight: 600, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 5, cursor: 'pointer' }}>
              ↩ Reactivate
            </button>
          )}
          {record.type === 'onboarding' && record.step_new_starter_form_at && (!record.m365_user_id || record.m365_provisioning_error) && (
            <button onClick={handleRetryM365} disabled={retrying}
              style={{ padding: '3px 10px', fontSize: 7, fontWeight: 600, background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE', borderRadius: 5, cursor: 'pointer', opacity: retrying ? 0.5 : 1 }}>
              {retrying ? '↻ Provisioning…' : '↻ Retry M365'}
            </button>
          )}
          {record.status !== 'cancelled' && (
            <button onClick={() => setEditSettings(p => !p)}
              style={{ padding: '3px 10px', fontSize: 7, fontWeight: 600, background: editSettings ? '#0D1D32' : '#F9FAFB', color: editSettings ? '#fff' : '#6B7280', border: '1px solid #E5E7EB', borderRadius: 5, cursor: 'pointer' }}>
              ✎ Edit Settings
            </button>
          )}
          <button onClick={() => { if (confirm('Delete this record?')) onDelete(record.id); }} style={{ padding: '3px 10px', fontSize: 7, color: '#9CA3AF', background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 5, cursor: 'pointer', marginLeft: 'auto' }}>
            Delete
          </button>
        </div>

        {/* Inline settings editor */}
        {editSettings && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: isDark ? 'rgb(15 23 42)' : '#F9FAFB', borderRadius: 8, border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              {record.type === 'onboarding' && (
                <>
                  <div>
                    <div style={{ fontSize: 7, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 3 }}>Clearance Required</div>
                    <select value={settings.clearance_target} onChange={e => setSettings(p => ({ ...p, clearance_target: e.target.value }))}
                      style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 5, padding: '4px 6px', fontSize: 9, fontFamily: 'Montserrat' }}>
                      <option value="sc">BPSS + SC</option>
                      <option value="bpss">BPSS Only</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 7, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 3 }}>M365 License</div>
                    <select value={settings.m365_license} onChange={e => setSettings(p => ({ ...p, m365_license: e.target.value }))}
                      style={{ width: '100%', border: '1px solid #D1D5DB', borderRadius: 5, padding: '4px 6px', fontSize: 9, fontFamily: 'Montserrat' }}>
                      <option value="standard">Business Standard</option>
                      <option value="premium">Business Premium</option>
                    </select>
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditSettings(false)} style={{ padding: '4px 10px', fontSize: 8, border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 5, background: isDark ? 'rgb(51 65 85)' : '#fff', color: isDark ? 'rgb(241 245 249)' : '#111', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveSettings} style={{ padding: '4px 10px', fontSize: 8, fontWeight: 700, background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', background: isDark ? 'rgb(15 23 42)' : undefined }}>
        <div style={{ fontSize: 8, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
          Progress
        </div>
        {renderSteps()}

        {/* Notes */}
        <div style={{ marginTop: 20, borderTop: `1px solid ${isDark ? 'rgb(51 65 85)' : '#F3F4F6'}`, paddingTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Notes</span>
            <button onClick={() => setEditNotes(p => !p)} style={{ fontSize: 8, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer' }}>
              {editNotes ? 'Cancel' : 'Edit'}
            </button>
          </div>
          {editNotes ? (
            <div>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                style={{ width: '100%', border: `1px solid ${isDark ? 'rgb(71 85 105)' : '#D1D5DB'}`, borderRadius: 5, padding: '5px 8px', fontSize: 9, fontFamily: 'Montserrat', boxSizing: 'border-box', resize: 'vertical', background: isDark ? 'rgb(30 41 59)' : '#fff', color: isDark ? 'rgb(241 245 249)' : '#111' }} />
              <button onClick={handleSaveNotes} style={{ marginTop: 6, padding: '4px 12px', fontSize: 8, fontWeight: 700, background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>Save</button>
            </div>
          ) : (
            <div style={{ fontSize: 9, color: record.notes ? (isDark ? 'rgb(203 213 225)' : '#374151') : (isDark ? 'rgb(71 85 105)' : '#D1D5DB'), fontStyle: record.notes ? 'normal' : 'italic' }}>
              {record.notes || 'No notes yet'}
            </div>
          )}
        </div>
      </div>

      {/* Complete step modal */}
      {completing && (
        <CompleteStepModal
          step={completing}
          record={record}
          assets={assets}
          onSave={handleComplete}
          onClose={() => setCompleting(null)}
        />
      )}
    </div>
  );
}

// ─── Record card (list view) ──────────────────────────────────────────────────
function RecordCard({ record, active, onClick }) {
  const isDark = useDarkMode();
  const steps = record.type === 'onboarding' ? ONBOARDING_STEPS : OFFBOARDING_STEPS;
  const visibleSteps = steps.filter(s => !s.scOnly || record.clearance_target === 'sc');
  const actionSteps  = visibleSteps.filter(s => !s.dateOnly);
  const doneCount    = actionSteps.filter(s => record[s.key]).length;
  const pct          = actionSteps.length > 0 ? Math.round((doneCount / actionSteps.length) * 100) : 0;
  const current      = actionSteps.find(s => !record[s.key])?.label || 'Complete';
  const isCancelled  = record.status === 'cancelled';
  const typeColor    = isCancelled ? '#9CA3AF' : record.type === 'onboarding' ? '#1D4ED8' : '#92400E';
  const typeBg       = isCancelled ? '#F3F4F6' : record.type === 'onboarding' ? '#EFF6FF' : '#FEF3C7';

  return (
    <div onClick={onClick}
      style={{
        background: isDark ? 'rgb(30 41 59)' : '#fff',
        border: `1px solid ${active ? (isDark ? 'rgb(148 163 184)' : '#0D1D32') : (isDark ? 'rgb(51 65 85)' : '#E5E7EB')}`,
        borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
        boxShadow: active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
        fontFamily: 'Montserrat, sans-serif',
      }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#0D1D32', marginBottom: 2 }}>
            {record.person_name}
          </div>
          <div style={{ fontSize: 8, color: '#9CA3AF', marginBottom: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ background: typeBg, color: typeColor, fontWeight: 700, padding: '1px 6px', borderRadius: 8, fontSize: 7 }}>
              {record.type === 'onboarding' ? '▲' : '▼'} {record.type}
            </span>
            <span>{record.person_role || '—'}</span>
          </div>
          {/* Mini progress bar */}
          <div style={{ height: 3, background: isDark ? 'rgb(51 65 85)' : '#F3F4F6', borderRadius: 2, marginBottom: 4 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#059669' : '#0D1D32', borderRadius: 2 }} />
          </div>
          <div style={{ fontSize: 7, color: '#9CA3AF', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: pct === 100 ? '#059669' : '#6B7280', fontWeight: pct === 100 ? 700 : 400 }}>
              {pct === 100 ? '✓ Complete' : `Next: ${current}`}
            </span>
            <span>{doneCount}/{actionSteps.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Offboarding modal — pick existing person ─────────────────────────────────
function NewOffboardingModal({ people, activeOnboardingIds, onSave, onClose }) {
  const isDark = useDarkMode();
  const [person_id, setPersonId] = useState('');
  const [search, setSearch] = useState('');
  const [leave_date, setLeaveDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const inp = {
    border: `1px solid ${isDark ? 'rgb(71 85 105)' : '#D1D5DB'}`,
    borderRadius: 5, padding: '5px 8px', fontSize: 10, fontFamily: 'Montserrat',
    width: '100%', boxSizing: 'border-box',
    background: isDark ? 'rgb(15 23 42)' : '#fff',
    color: isDark ? 'rgb(241 245 249)' : '#111',
  };
  const lbl = { display: 'block', fontSize: 8, fontWeight: 700, color: isDark ? 'rgb(148 163 184)' : '#6B7280', textTransform: 'uppercase', marginBottom: 3 };
  const field = { marginBottom: 12 };

  // Only show active employees who are NOT currently onboarding
  const active = people.filter(p =>
    p.employee_status === 'active' &&
    !activeOnboardingIds.has(p.id)
  );
  const filtered = search.trim()
    ? active.filter(p => `${p.first_name} ${p.last_name} ${p.designation || ''}`.toLowerCase().includes(search.toLowerCase()))
    : active;

  const selectedPerson = people.find(p => p.id === person_id);

  const handleSave = async () => {
    if (!person_id) { setErr('Please select a person'); return; }
    setSaving(true);
    setErr('');
    try {
      await onSave({ person_id, leave_date, notes });
    } catch (e) {
      setErr(e.message || 'Failed to create record');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: isDark ? 'rgb(30 41 59)' : '#fff', borderRadius: 12, width: 440, padding: '20px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: isDark ? '1px solid rgb(51 65 85)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#0D1D32' }}>Start Offboarding</div>
            <div style={{ fontSize: 9, color: isDark ? 'rgb(100 116 139)' : '#9CA3AF', marginTop: 2 }}>Select the person who is leaving</div>
          </div>
          <div style={{ padding: '3px 10px', borderRadius: 20, background: '#FEF3C7', color: '#92400E', fontSize: 8, fontWeight: 700 }}>OFFBOARDING</div>
        </div>

        {/* Person search + list */}
        <div style={field}>
          <label style={lbl}>Find Person</label>
          <input value={search} onChange={e => setSearch(e.target.value)} style={inp} placeholder="Search by name or job title…" />
        </div>

        {!selectedPerson ? (
          <div style={{ border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginBottom: 12, background: isDark ? 'rgb(15 23 42)' : undefined }}>
            {filtered.length === 0 && (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: 9, color: '#9CA3AF' }}>No people found</div>
            )}
            {filtered.map(p => (
              <div key={p.id} onClick={() => { setPersonId(p.id); setSearch(''); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', cursor: 'pointer', borderBottom: `1px solid ${isDark ? 'rgb(30 41 59)' : '#F3F4F6'}` }}
                onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgb(30 41 59)' : '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = isDark ? 'transparent' : ''}>
                <Avatar personId={p.id} style={{ width: 26, height: 26, borderRadius: '50%', background: p.avatar_color || '#A1723D', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#fff' }}>
                  {p.first_name[0]}{p.last_name[0]}
                </Avatar>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: isDark ? 'rgb(241 245 249)' : '#111827' }}>{p.first_name} {p.last_name}</div>
                  <div style={{ fontSize: 8, color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>{p.designation || '—'}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: isDark ? 'rgb(15 23 42)' : '#F0F4F8', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
            <Avatar personId={selectedPerson.id} style={{ width: 32, height: 32, borderRadius: '50%', background: selectedPerson.avatar_color || '#A1723D', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>
              {selectedPerson.first_name[0]}{selectedPerson.last_name[0]}
            </Avatar>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? 'rgb(241 245 249)' : '#0D1D32' }}>{selectedPerson.first_name} {selectedPerson.last_name}</div>
              <div style={{ fontSize: 9, color: isDark ? 'rgb(148 163 184)' : '#6B7280' }}>{selectedPerson.designation || '—'}</div>
            </div>
            <button onClick={() => setPersonId('')} style={{ fontSize: 8, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Change</button>
          </div>
        )}

        <div style={field}>
          <label style={lbl}>Last Working Day</label>
          <input type="date" value={leave_date} onChange={e => setLeaveDate(e.target.value)} style={inp} />
        </div>
        <div style={{ ...field, marginBottom: 16 }}>
          <label style={lbl}>Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={inp} placeholder="Reason for leaving, handover instructions…" />
        </div>

        {err && <div style={{ color: '#DC2626', fontSize: 9, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '7px 0', fontSize: 9, border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, background: isDark ? 'rgb(51 65 85)' : '#fff', color: isDark ? 'rgb(241 245 249)' : '#111', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !person_id} style={{ flex: 2, padding: '7px 0', fontSize: 9, fontWeight: 700, background: '#B8966E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: (saving || !person_id) ? 0.4 : 1 }}>
            {saving ? 'Creating…' : '✓ Start Offboarding Process'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function OnboardingPage() {
  const isDark = useDarkMode();
  const [records,     setRecords]     = useState([]);
  const [teams,       setTeams]       = useState([]);
  const [people,      setPeople]      = useState([]);
  const [assets,      setAssets]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [showNew,     setShowNew]     = useState(false);
  const [showOffboard, setShowOffboard] = useState(false);
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  const load = useCallback(async () => {
    try {
      const [recs, teamsData, peopleData, assets] = await Promise.all([
        api.get('onboarding'),
        api.get('teams'),
        api.get('people'),
        api.get('inventory'),
      ]);
      setRecords(recs || []);
      setTeams(teamsData || []);
      setPeople(peopleData || []);
      setAssets(assets?.items || assets || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createRecord = async ({ person, process }) => {
    // Step 1: Create the person record
    const newPerson = await api.post('people', person);
    // Step 2: Create the onboarding record (always 'onboarding' from this flow)
    const startDate = person.date_of_joining || '';
    const rec = await api.post('onboarding', {
      person_id: newPerson.id,
      type: 'onboarding',
      clearance_target: process.clearance_target,
      m365_license: process.m365_license || 'standard',
      notes: process.notes || null,
      probation_3month_date: process.probation_3month_date || addMonths(startDate, 3) || null,
      probation_6month_date: process.probation_6month_date || addMonths(startDate, 6) || null,
    });
    setRecords(prev => [rec, ...prev]);
    setSelected(rec.id);
    setShowNew(false);
  };

  const createOffboardingRecord = async ({ person_id, leave_date, notes }) => {
    const rec = await api.post('onboarding', {
      person_id,
      type: 'offboarding',
      leave_date: leave_date || null,
      notes: notes || null,
    });
    setRecords(prev => [rec, ...prev]);
    setSelected(rec.id);
    setShowOffboard(false);
  };

  const updateRecord = (updated) => {
    setRecords(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  const deleteRecord = async (id) => {
    await api.delete(`onboarding/${id}`);
    setRecords(prev => prev.filter(r => r.id !== id));
    setSelected(null);
  };

  const filtered = records.filter(r => {
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (statusFilter === 'active') return r.status === 'in_progress' || r.status === 'on_hold';
    if (statusFilter === 'complete') return r.status === 'complete';
    if (statusFilter === 'cancelled') return r.status === 'cancelled';
    return true;
  });

  const selectedRecord = records.find(r => r.id === selected);

  const counts = {
    onboarding: records.filter(r => r.type === 'onboarding' && (r.status === 'in_progress' || r.status === 'on_hold')).length,
    offboarding: records.filter(r => r.type === 'offboarding' && (r.status === 'in_progress' || r.status === 'on_hold')).length,
    complete: records.filter(r => r.status === 'complete').length,
  };

  const pillStyle = (active) => ({
    padding: '3px 12px', borderRadius: 12, fontSize: 9, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'Montserrat',
    background: active ? '#0D1D32' : isDark ? 'rgb(30 41 59)' : '#F3F4F6',
    color: active ? '#fff' : isDark ? 'rgb(148 163 184)' : '#6B7280',
    border: isDark ? `1px solid ${active ? 'transparent' : 'rgb(51 65 85)'}` : 'none',
  });

  return (
    <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 11, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: isDark ? 'rgb(241 245 249)' : '#0D1D32' }}>Onboarding & Offboarding</div>
          <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 2 }}>Track the full journey for new starters and leavers</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowNew(true)}
            style={{ padding: '7px 14px', fontSize: 9, fontWeight: 700, background: '#0D1D32', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: 'Montserrat' }}>
            + New Onboarding
          </button>
          <button onClick={() => setShowOffboard(true)}
            style={{ padding: '7px 14px', fontSize: 9, fontWeight: 700, background: '#B8966E', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: 'Montserrat' }}>
            + New Offboarding
          </button>

        </div>
      </div>

      {/* ── Summary tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12, flexShrink: 0 }}>
        {[
          ['Active Onboardings', counts.onboarding, '#EFF6FF', '#1D4ED8'],
          ['Active Offboardings', counts.offboarding, '#FEF3C7', '#92400E'],
          ['Completed', counts.complete, '#D1FAE5', '#065F46'],
        ].map(([label, count, bg, color]) => (
          <div key={label} style={{ background: isDark ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${isDark ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 7, color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color }}>{count}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexShrink: 0, flexWrap: 'wrap' }}>
        <button style={pillStyle(typeFilter === 'all')} onClick={() => setTypeFilter('all')}>All</button>
        <button style={pillStyle(typeFilter === 'onboarding')} onClick={() => setTypeFilter('onboarding')}>▲ Onboarding</button>
        <button style={pillStyle(typeFilter === 'offboarding')} onClick={() => setTypeFilter('offboarding')}>▼ Offboarding</button>
        <div style={{ width: 1, background: isDark ? 'rgb(51 65 85)' : '#E5E7EB', margin: '0 4px' }} />
        <button style={pillStyle(statusFilter === 'active')} onClick={() => setStatusFilter('active')}>Active</button>
        <button style={pillStyle(statusFilter === 'complete')} onClick={() => setStatusFilter('complete')}>Completed</button>
        <button style={pillStyle(statusFilter === 'cancelled')} onClick={() => setStatusFilter('cancelled')}>Cancelled</button>
        <button style={pillStyle(statusFilter === 'all')} onClick={() => setStatusFilter('all')}>All</button>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', flex: 1, gap: 0, overflow: 'hidden', minHeight: 0 }}>
        {/* List */}
        <div style={{ width: selected ? 300 : '100%', flexShrink: 0, overflowY: 'auto', paddingRight: selected ? 8 : 0, borderRight: selected ? `1px solid ${isDark ? 'rgb(51 65 85)' : '#F3F4F6'}` : 'none' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 9 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 9 }}>
              No records. Click "+ New Record" to start tracking an onboarding or offboarding.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filtered.map(r => (
                <RecordCard key={r.id} record={r} active={selected === r.id} onClick={() => setSelected(r.id)} />
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        {selectedRecord && (
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <RecordDetail
              key={selectedRecord.id}
              record={selectedRecord}
              assets={assets}
              onUpdate={updateRecord}
              onClose={() => setSelected(null)}
              onDelete={deleteRecord}
            />
          </div>
        )}
      </div>

      {showNew && (
        <NewRecordModal teams={teams} onSave={createRecord} onClose={() => setShowNew(false)} />
      )}
      {showOffboard && (
        <NewOffboardingModal
          people={people}
          activeOnboardingIds={new Set(records.filter(r => r.type === 'onboarding' && (r.status === 'in_progress' || r.status === 'on_hold')).map(r => r.person_id))}
          onSave={createOffboardingRecord}
          onClose={() => setShowOffboard(false)}
        />
      )}
    </div>
  );
}
